import React, { useState, useCallback, useMemo, useRef } from "react";
import { Link } from "react-router-dom";
import { useGetBlogsQuery, useGetBlogCategoriesQuery } from "../../redux/api/blogsApi";
import { motion, AnimatePresence } from "framer-motion";
import Loader from "../layout/Loader";
import { Helmet } from "react-helmet-async";
import { ChevronDown, ArrowRight } from "lucide-react";
import RevealSection from "../layout/RevealSection";

const BLOGS_PER_PAGE = 12;

// ─── BlogsPage ────────────────────────────────────────────────────────────────

const BlogsPage = () => {
  const [page, setPage] = useState(1);
  const [selectedCategory, setSelectedCategory] = useState("");
  const [expandedBlog, setExpandedBlog] = useState(null);
  const categoryBarRef = useRef(null);

  const queryParams = useMemo(() => {
    const params = { page };
    if (selectedCategory) params.category = selectedCategory;
    return params;
  }, [page, selectedCategory]);

  const { data, isLoading, isFetching, error } = useGetBlogsQuery(queryParams);

  // Dedicated categories endpoint — returns [{ name, count }] from DB aggregation
  const { data: categoriesData } = useGetBlogCategoriesQuery();
  const categoriesWithCounts = categoriesData?.categories ?? [];
  const totalAllBlogs = categoriesWithCounts.reduce((sum, c) => sum + c.count, 0);

  const toggleBlog = useCallback((blogId) => {
    setExpandedBlog((prev) => (prev === blogId ? null : blogId));
  }, []);

  const handleKeyPress = useCallback(
    (e, blogId) => {
      if (e.key === "Enter" || e.key === " ") {
        e.preventDefault();
        toggleBlog(blogId);
      }
    },
    [toggleBlog]
  );

  const handleCategoryChange = useCallback((cat) => {
    setSelectedCategory(cat);
    setPage(1);
    setExpandedBlog(null);
    categoryBarRef.current?.scrollIntoView({ behavior: "smooth", block: "nearest" });
  }, []);

  const handlePageChange = (newPage) => {
    setPage(newPage);
    setExpandedBlog(null);
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const blogs = data?.blogs ?? [];
  const total = data?.total ?? 0;
  const totalPages = Math.ceil(total / BLOGS_PER_PAGE);

  if (isLoading) return <Loader />;

  if (error) {
    return (
      <div className="max-w-6xl mx-auto lg:mt-44 mt-28 px-4 sm:px-6 lg:px-8">
        <div className="text-center py-12">
          <p className="text-red-500 text-lg">
            {error?.data?.message || "Failed to load blogs"}
          </p>
          <button
            onClick={() => window.location.reload()}
            className="mt-4 px-6 py-2 bg-[#BC3F4A] text-white rounded-lg hover:bg-[#a03540] transition"
          >
            Retry
          </button>
        </div>
      </div>
    );
  }

  return (
    <>
      <Helmet>
        <title>Blog — Sanguine Accent</title>
        <meta
          name="description"
          content="Explore stories about fine art photography, Ukrainian culture, string art, and the mission behind Sanguine Accent."
        />
        <meta name="robots" content="index, follow" />
        <link rel="canonical" href="https://sanguineaccent.com/blogs" />
      </Helmet>

      {/* ── Category Bar ──────────────────────────────────────────────────── */}
      <div ref={categoryBarRef} className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 mt-28 lg:mt-44">
        <div className="flex flex-wrap gap-2 py-4">
            <CategoryPill
              label="All"
              count={totalAllBlogs}
              active={selectedCategory === ""}
              onClick={() => handleCategoryChange("")}
            />
            {categoriesWithCounts.map(({ name, count }) => (
              <CategoryPill
                key={name}
                label={name}
                count={count}
                active={selectedCategory === name}
                onClick={() =>
                  handleCategoryChange(selectedCategory === name ? "" : name)
                }
              />
            ))}
        </div>
      </div>

      <div className="max-w-6xl mx-auto mt-4 px-4 sm:px-6 lg:px-8 mb-20">
        {/* Page Header */}
        <div className="mb-6">
          <h1 className="text-3xl md:text-4xl font-playfair text-[#1c1917] mb-1">
            {selectedCategory ? selectedCategory : "All Articles"}
          </h1>
          <p className="text-stone-500 font-sourcesans text-sm">
            {selectedCategory ? (
              <>
                {total} {total === 1 ? "article" : "articles"} in{" "}
                <span className="text-[#BC3F4A] font-medium">{selectedCategory}</span>{" "}
                ·{" "}
                <button
                  onClick={() => handleCategoryChange("")}
                  className="underline hover:text-[#BC3F4A] transition-colors"
                >
                  Clear filter
                </button>
              </>
            ) : (
              <>
                {total} {total === 1 ? "article" : "articles"} total
              </>
            )}
          </p>
        </div>

        {/* Empty state */}
        {blogs.length === 0 ? (
          <div className="text-center py-16">
            <p className="text-stone-500 text-lg">
              {selectedCategory
                ? `No articles found in "${selectedCategory}".`
                : "No articles available yet."}
            </p>
            {selectedCategory && (
              <button
                onClick={() => handleCategoryChange("")}
                className="mt-4 text-[#BC3F4A] underline text-sm"
              >
                Show all articles
              </button>
            )}
          </div>
        ) : (
          <>
            {/* Blogs List */}
            <div
              className={`divide-y divide-stone-200 border border-stone-200 rounded-2xl bg-[#f9f8f4]/60 shadow-sm overflow-hidden transition-opacity duration-200 ${
                isFetching ? "opacity-60 pointer-events-none" : "opacity-100"
              }`}
            >
              {blogs.map((blog, index) => {
                const isOpen = expandedBlog === blog._id;
                const imageUrl = blog.images?.[0]?.url;
                const strippedDescription =
                  blog.description?.replace(/<[^>]+>/g, "") || "";
                const truncatedDescription = strippedDescription.slice(0, 300);
                const formattedDate = new Date(blog.createdAt).toLocaleDateString(
                  "en-US",
                  { year: "numeric", month: "short", day: "numeric" }
                );

                return (
                  <article
                    key={blog._id}
                    className="hover:bg-white/80 transition-colors duration-200"
                  >
                    {/* Row header */}
                    <RevealSection delay={0.05}>
                    <div
                      className="flex justify-between items-center py-4 px-4 sm:px-6 cursor-pointer select-none"
                      onClick={() => toggleBlog(blog._id)}
                      onKeyDown={(e) => handleKeyPress(e, blog._id)}
                      role="button"
                      tabIndex={0}
                      aria-expanded={isOpen}
                      aria-controls={`blog-content-${blog._id}`}
                    >
                      
                      <div className="flex-1 min-w-0 pr-4">
                        <h2 className="font-playfair text-lg md:text-xl text-[#1c1917] mb-1 truncate">
                          {blog.name}
                        </h2>
                        <div className="flex items-center gap-3 flex-wrap">
                          {blog.category && (
                            <button
                              onClick={(e) => {
                                e.stopPropagation();
                                handleCategoryChange(
                                  selectedCategory === blog.category
                                    ? ""
                                    : blog.category
                                );
                              }}
                              className={`inline-block text-xs md:text-sm font-medium px-3 py-1 rounded-full transition-colors ${
                                selectedCategory === blog.category
                                  ? "bg-[#BC3F4A] text-white"
                                  : "text-[#BC3F4A] bg-[#BC3F4A]/10 hover:bg-[#BC3F4A]/20"
                              }`}
                            >
                              {blog.category}
                            </button>
                          )}
                          <time
                            className="text-sm text-stone-500"
                            dateTime={blog.createdAt}
                          >
                            {formattedDate}
                          </time>
                        </div>
                      </div>

                      <div className="flex-shrink-0">
                        <motion.div
                          animate={{ rotate: isOpen ? 180 : 0 }}
                          transition={{ duration: 0.3 }}
                        >
                          <ChevronDown className="w-5 h-5 text-stone-400" aria-hidden="true" />
                        </motion.div>
                      </div>
                    </div>
                    </RevealSection>

                    {/* Expandable content */}
                    <AnimatePresence initial={false}>
                      {isOpen && (
                        <motion.div
                          id={`blog-content-${blog._id}`}
                          initial={{ height: 0, opacity: 0 }}
                          animate={{ height: "auto", opacity: 1 }}
                          exit={{ height: 0, opacity: 0 }}
                          transition={{ duration: 0.3, ease: "easeInOut" }}
                          className="overflow-hidden bg-white border-t border-stone-200"
                        >
                          <div className="p-4 sm:p-6">
                            {imageUrl && (
                              <div className="mb-6 rounded-lg overflow-hidden bg-stone-100">
                                <img
                                  src={imageUrl}
                                  alt={blog.name}
                                  className="w-full h-64 md:h-80 object-cover"
                                  loading={index < 3 ? "eager" : "lazy"}
                                  decoding="async"
                                />
                              </div>
                            )}
                            <div className="prose prose-stone max-w-none mb-6">
                              <p className="text-stone-700 font-sourcesans text-sm md:text-base leading-relaxed">
                                {truncatedDescription}
                                {strippedDescription.length > 300 && "..."}
                              </p>
                            </div>
                            <div className="flex justify-end">
                              <Link
                                to={`/blogs/${blog._id}`}
                                className="inline-flex items-center gap-2 text-[#BC3F4A] text-sm md:text-base font-medium hover:underline transition-all"
                                onClick={(e) => e.stopPropagation()}
                              >
                                Read Full Article
                                <ArrowRight className="w-4 h-4" aria-hidden="true" />
                              </Link>
                            </div>
                          </div>
                        </motion.div>
                      )}
                    </AnimatePresence>
                  </article>
                );
              })}
            </div>

            {/* Pagination */}
            {totalPages > 1 && (
              <Pagination
                page={page}
                totalPages={totalPages}
                onPageChange={handlePageChange}
                isFetching={isFetching}
              />
            )}

            <div className="mt-6 text-center">
              <p className="text-sm text-stone-400 font-sourcesans">
                Page {page} of {totalPages} · {total}{" "}
                {total === 1 ? "article" : "articles"} total
              </p>
            </div>
          </>
        )}
      </div>
    </>
  );
};

// ─── CategoryPill ─────────────────────────────────────────────────────────────

const CategoryPill = ({ label, count, active, onClick }) => (
  <button
    onClick={onClick}
    className={`flex-shrink-0 inline-flex items-center gap-1.5 px-4 py-1.5 rounded-full text-sm font-medium border transition-all duration-200 ${
      active
        ? label === "All"
          ? "bg-[#1c1917] text-white border-[#1c1917]"
          : "bg-[#BC3F4A] text-white border-[#BC3F4A]"
        : "bg-white text-stone-600 border-stone-300 hover:border-[#BC3F4A] hover:text-[#BC3F4A]"
    }`}
  >
    {label}
    <span
      className={`text-xs px-1.5 py-0.5 rounded-full font-normal ${
        active ? "bg-white/20 text-white" : "bg-stone-100 text-stone-500"
      }`}
    >
      {count}
    </span>
  </button>
);

// ─── Pagination ───────────────────────────────────────────────────────────────

const Pagination = ({ page, totalPages, onPageChange, isFetching }) => {
  const pages = useMemo(() => {
    if (totalPages <= 7)
      return Array.from({ length: totalPages }, (_, i) => i + 1);
    const result = [];
    result.push(1);
    if (page > 3) result.push("...");
    for (
      let i = Math.max(2, page - 1);
      i <= Math.min(totalPages - 1, page + 1);
      i++
    ) {
      result.push(i);
    }
    if (page < totalPages - 2) result.push("...");
    result.push(totalPages);
    return result;
  }, [page, totalPages]);

  return (
    <div className="flex items-center justify-center gap-1 mt-8 flex-wrap">
      <button
        onClick={() => onPageChange(page - 1)}
        disabled={page === 1 || isFetching}
        className="px-3 py-2 rounded-lg text-sm text-stone-600 border border-stone-200 hover:border-stone-400 disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
        aria-label="Previous page"
      >
        ← Prev
      </button>

      {pages.map((p, i) =>
        p === "..." ? (
          <span key={`ellipsis-${i}`} className="px-2 text-stone-400 select-none">
            …
          </span>
        ) : (
          <button
            key={p}
            onClick={() => onPageChange(p)}
            disabled={isFetching}
            className={`w-9 h-9 rounded-lg text-sm font-medium border transition-colors ${
              p === page
                ? "bg-[#BC3F4A] text-white border-[#BC3F4A]"
                : "bg-white text-stone-600 border-stone-200 hover:border-[#BC3F4A] hover:text-[#BC3F4A]"
            } disabled:opacity-50`}
            aria-label={`Page ${p}`}
            aria-current={p === page ? "page" : undefined}
          >
            {p}
          </button>
        )
      )}

      <button
        onClick={() => onPageChange(page + 1)}
        disabled={page === totalPages || isFetching}
        className="px-3 py-2 rounded-lg text-sm text-stone-600 border border-stone-200 hover:border-stone-400 disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
        aria-label="Next page"
      >
        Next →
      </button>
    </div>
  );
};

export default BlogsPage;